<script setup>
import { computed } from 'vue'
import buttonContracts from '~/assets/icon_contracts.svg'
import buttonPuzzle from '~/assets/button_puzzle.svg'
import buttonCalendar from '~/assets/button_calendar.svg'
import { useRouter } from 'vue-router'
import { useAuthStore, useAssessmentsStore, useAppStore, useReadinessStore } from '../stores'
import { notify } from 'notiwind';

const authStore = useAuthStore()
const assessmentsStore = useAssessmentsStore()
const readinessStore = useReadinessStore()
const router = useRouter()

const props = defineProps({
  child: {
    type: Object,
    required: true
  },
  detailView: Boolean,
})
const textColor = computed(() => {
  const colorScheme = ['text-primary', 'text-secondary-accent']
  // return `${colorScheme[Math.floor(Math.random() * colorScheme.length)]}`
  return colorScheme[Math.floor(Math.random() * colorScheme.length)]
})

const readinessClickHandler = () => {
  authStore.currentChildId = props.child.id
  router.push(`/readiness`)
}

const assesssmentClickHandler = () => {
  if (props.child.current_readiness_report_card && props.child.current_readiness_report_card.current_readiness_assessment_id) {
    router.push(`/readiness/assessments/${props.child.current_readiness_report_card.current_readiness_assessment_id}`)
  } else if (props.child.current_readiness_report_card) {
    assessmentsStore.createReadinessAssessment(props.child.id, props.child.current_readiness_report_card.id)
  } else {
    notify({
      group: "foo",
      type: "error",
      title: "Ruh roh!",
      text: "Try starting a new Readiness Report first!"
    }, 4000)
  }
}

const goToChildResources = (id) => {
  readinessStore.clearChildFilters()
  readinessStore.toggleChildFilter(id)
  router.push(`/resources/activities`)
}

</script>
<template>
  <div class="min-w-full flex justify-center content-center ">
    <div
      class="block mx-2 w-96 max-w-md px-2 py-4 bg-white rounded-xl shadow-md flex flex-col justify-start content-center mb-2">
      <!-- this works because flex row is the default -->
      <div class="flex" :class="{ 'flex-col': !props.detailView }">
        <div :class="`flex justify-start ${props.detailView ? 'items-center' : 'items-start'}`">
          <div :class="`flex items-center justify-start h-24 bg-gray-300 rounded-xl dark:bg-gray-700 justify-self-start`">
            <svg class="w-[5.5rem] h-16 text-gray-200 dark:text-gray-600" aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 20">
              <path
                d="M14.066 0H7v5a2 2 0 0 1-2 2H0v11a1.97 1.97 0 0 0 1.934 2h12.132A1.97 1.97 0 0 0 16 18V2a1.97 1.97 0 0 0-1.934-2ZM10.5 6a1.5 1.5 0 1 1 0 2.999A1.5 1.5 0 0 1 10.5 6Zm2.221 10.515a1 1 0 0 1-.858.485h-8a1 1 0 0 1-.9-1.43L5.6 10.039a.978.978 0 0 1 .936-.57 1 1 0 0 1 .9.632l1.181 2.981.541-1a.945.945 0 0 1 .883-.522 1 1 0 0 1 .879.529l1.832 3.438a1 1 0 0 1-.031.988Z" />
              <path d="M5 5V.13a2.96 2.96 0 0 0-1.293.749L.879 3.707A2.98 2.98 0 0 0 .13 5H5Z" />
            </svg>
          </div>
          <div class="flex flex-col max-h-24 " :class="{ 'pl-2': props.detailView }">
            <div class="flex" :class="{ 'flex-col text-sm': props.detailView }">
              <h5 :class="`text-lg font-bold ${textColor} ${props.detailView ? 'pt-1' : 'pt-4 px-2'}`">
                {{ props.child.first_name }}
              </h5>
              <div :class="{ 'pt-4 font-bold': !props.detailView }">
                {{ child.years_old }} years old
              </div>
              <div v-if="props.detailView">
                {{ child.gender }}
              </div>
            </div>
            <p :class="`text-sm ${props.detailView ? 'font-bold text-black' : 'pl-2'}`"
              v-if="props.detailView && props.child.current_readiness_report_card">
              {{ props.child.current_readiness_report_card.short_title }}
            </p>
            <div v-else-if="!props.detailView && props.child.current_readiness_report_card"
              class="font-normal text-sm font-serif text-gray-700  max-h-fit pb-1.5 text-clip"
              :class="{ 'pl-2': !props.detailView }">
              <div>
                {{ props.child.current_readiness_report_card.incomplete_milestones.length > 0 ?
                `${props.child.current_readiness_report_card.incomplete_milestones.length} milestones remaining.` :
                  'All milestones achieved 😎.' }}
              </div>
              <div v-if="useAppStore.showContracts">
                {{ props.child.contracts.length > 0 ?
                `${props.child.contracts.length} open contracts.` :
                  'All contracts complete 😎.' }}
              </div>
            </div>
            <!-- TODO: clean this up -->
            <div v-else-if="!props.detailView && !props.child.current_readiness_report_card"
              class="font-normal text-sm font-serif text-gray-700  max-h-fit pb-1.5 text-clip"
              :class="{ 'pl-2': !props.detailView }">
              <div>
                No readiness report card yet.
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row justify-start items-center py-2" >
          <!-- could jump straight into readiness assessment -->
          <button @click="readinessClickHandler" v-if="!props.detailView" 
            :class="`basis-2/5 self-center bg-light-grey focus:ring-4 focus:ring-primary rounded-lg  py-3 focus:outline-none text-lg font-black ${textColor}`"
          type="button">Readiness</button>
          <img :class="`w-10 h-10 justify-self-end cursor-pointer ${props.detailView ? 'ml-1' : 'mx-1'}`"
            aria-hidden="true" @click="router.push('/contracts')" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 20 20" :src="buttonContracts" v-if="useAppStore.showContracts"/>
          <img :class="`w-10 h-10 justify-self-end cursor-pointer ${props.detailView ? 'ml-1' : 'mx-1'}`"
            aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" :src="buttonPuzzle"
            @click="goToChildResources(props.child.id)" />
          <img :class="`w-10 h-10 justify-self-end cursor-pointer ${props.detailView ? 'ml-1' : 'mx-1'}`"
            aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" :src="buttonCalendar"
            @click="router.push('/calendar')" />
      </div>
    </div>
  </div>
</div></template>